import type { VoidFunctionComponent } from 'react';

import { formatCount } from '../../../../shared/utils/numbers';
import { useFossabotPRCounts } from '../../../hooks/api/fossabot/useFossabotPRCounts';
import Icon, { icons } from '../../generic/Icon';
import Tooltip from '../../generic/Tooltip';
import type { FossabotPRCounts } from '../../../../shared/fossabot/common';
import type { IconClassName } from '../../generic/Icon';

import styles from './FossabotPrStatCards.module.less';

type StatCard = {
  label: string;
  /** Hover text spelling out what the card counts. */
  tooltip: string;
  countKey: keyof FossabotPRCounts;
  icon?: IconClassName;
  /** Resolved CSS-module class colouring the icon. */
  iconClass?: string;
};

/*
 * The analysis counts (analyzed, needsReview) measure a different axis from the
 * VCS states, so they overlap the open count and must never be read as a
 * breakdown of the total.
 */
const STAT_CARDS: StatCard[] = [
  {
    label: 'Total PRs',
    tooltip: 'All PRs opened by fossabot for this project.',
    countKey: 'total',
  },
  {
    label: 'Open',
    tooltip: 'PRs that are currently open, regardless of analysis result.',
    countKey: 'open',
    icon: icons.codePullRequest,
    iconClass: styles.iconGreen,
  },
  {
    label: 'Merged',
    tooltip: 'PRs that have been merged.',
    countKey: 'merged',
    icon: icons.codeMerge,
    iconClass: styles.iconGreen,
  },
  {
    label: 'Recommended to Merge',
    tooltip: 'Open PRs fossabot recommends merging, including those with fixes applied. Excludes merged and closed PRs.',
    countKey: 'analyzed',
    icon: icons.check,
    iconClass: styles.iconGreen,
  },
  {
    label: 'Needs Review',
    tooltip: 'Open PRs with concerns or an inconclusive analysis. Excludes merged and closed PRs.',
    countKey: 'needsReview',
    icon: icons.messageCode,
    iconClass: styles.iconAmber,
  },
];

/** Count shown while the counts are in flight. */
const LOADING_COUNT = '...';

/** Count shown once the counts request has failed, matching SubnavCountBadge. */
const UNAVAILABLE_COUNT = '--';

/*
 * Hover and screen-reader text for the failed state. A lone "--" carries no
 * meaning on its own, and screen readers routinely skip it, so the failure has
 * to be spelled out. Matches the wording on the subnav badge.
 */
const UNAVAILABLE_TITLE = 'PR counts unavailable';

type Props = {
  /**
   * The project locator, without the revision ID.
   * @example `custom+17/cool-project`
   */
  projectLocator: string;
};

/**
 * The five display-only totals at the top of the fossabot PRs page. They are
 * deliberately not clickable: the filter chips on the table header own state
 * filtering, and two of these five cards are analysis states with no filter
 * behind them.
 *
 * The counts are fetched here rather than passed down, matching the counted
 * subnav items, so this stays behind the page's own gates. The subnav badge
 * shares the request through the same query key.
 */
export const FossabotPrStatCards: VoidFunctionComponent<Props> = ({ projectLocator }) => {
  const { data: counts, isLoading, isError } = useFossabotPRCounts(projectLocator);

  const getCountText = (countKey: keyof FossabotPRCounts): string => {
    if (isLoading) {
      return LOADING_COUNT;
    }
    if (isError) {
      return UNAVAILABLE_COUNT;
    }
    return formatCount(counts?.[countKey] ?? 0);
  };

  return (
    <div className={styles.cards} aria-busy={isLoading}>
      {STAT_CARDS.map(({ label, tooltip, countKey, icon, iconClass }) => (
        <div className={styles.card} key={label} data-testid={`stat-card-${countKey}`}>
          <Tooltip id={`stat-card-tooltip-${countKey}`} tooltip={tooltip}>
            <div className={styles.label}>
              {icon && (
                <span className={iconClass}>
                  <Icon icon={icon} testId={`stat-card-icon-${countKey}`} />
                </span>
              )}
              <span>{label}</span>
            </div>
          </Tooltip>
          <div className={styles.count} title={isError ? UNAVAILABLE_TITLE : undefined}>
            {getCountText(countKey)}
            {isError && <span className="sr-only">{UNAVAILABLE_TITLE}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};
